import './ToggleSwitch.scss';
import cn from 'classnames';
import { ComponentProps, Dispatch, PropsWithChildren, ReactNode, SetStateAction } from 'react';
import { Button } from '../Button';
import { Appearance, Background, Design, Size, Placement, PlacementType } from '../designConstants';
import { InfoTooltip } from '../InfoTooltip';
import { Spinner } from '../Spinner';

export const LabelPosition = {
  LEADING: 'leading',
  TRAILING: 'trailing',
  NONE: 'none'
} as const;
export type LabelPositionType = (typeof LabelPosition)[keyof typeof LabelPosition];

// Extend Button's prop surface so callers can pass through any button HTML
// attribute (id, data-*, aria-*, etc.) plus the shared design-system props
// without ToggleSwitch re-declaring each. Omit the props ToggleSwitch owns or
// repurposes: `children` becomes `label`, `onClick` becomes `onChange` (with a
// boolean-setter signature), and the leading / trailing element slots are
// reserved for the toggle icon.
interface ToggleSwitchProps extends Omit<
  ComponentProps<typeof Button>,
  | 'children'
  | 'onClick'
  | 'onChange'
  | 'leadingElement'
  | 'trailingElement'
  | 'leadingElementIsInteractive'
  | 'trailingElementIsInteractive'
> {
  label: string;
  checked: boolean;
  onChange: Dispatch<SetStateAction<boolean>>;
  labelPosition?: LabelPositionType;
  infoTooltipText?: ReactNode;
  infoTooltipPlacement?: PlacementType;
}

export const ToggleSwitch = ({
  label,
  checked,
  onChange,
  labelPosition = LabelPosition.LEADING,
  appearance = Appearance.FILLED,
  size = Size.MD,
  design = Design.NEUTRAL,
  background = Background.LIGHT,
  disabled,
  loading,
  infoTooltipText,
  infoTooltipPlacement = Placement.AUTO,
  className,
  ...buttonPassthroughProps
}: ToggleSwitchProps) => {
  const showInfoTooltip = Boolean(infoTooltipText) && labelPosition !== LabelPosition.NONE;
  const toggleIcon = (
    <ToggleIcon checked={checked} loading={loading} hasInfoTooltip={showInfoTooltip} />
  );

  return (
    <InfoTooltipOverlay
      show={showInfoTooltip}
      text={infoTooltipText}
      placement={infoTooltipPlacement}
    >
      <Button
        {...buttonPassthroughProps}
        className={cn('bpm-toggle-switch', { checked }, className)}
        size={size}
        design={design}
        appearance={appearance}
        background={background}
        onClick={() => onChange(current => !current)}
        leadingElement={labelPosition === LabelPosition.TRAILING ? toggleIcon : undefined}
        trailingElement={labelPosition === LabelPosition.LEADING ? toggleIcon : undefined}
        disabled={disabled || loading}
      >
        {label}
      </Button>
    </InfoTooltipOverlay>
  );
};

interface InfoTooltipOverlayProps extends PropsWithChildren {
  show: boolean;
  text: ReactNode;
  placement: PlacementType;
}

// InfoTooltip renders a <button>, so it is drawn over the toggle's <button>, on the spacer that
// ToggleIcon leaves for it, instead of nested inside it.
const InfoTooltipOverlay = ({ children, show, text, placement }: InfoTooltipOverlayProps) => {
  if (!show) {
    return children;
  }

  return (
    <span className='bpm-toggle-switch-with-info'>
      {children}
      <span className='bpm-btn-overlay toggle-switch-info'>
        <InfoTooltip size={Size.SM} placement={placement}>
          {text}
        </InfoTooltip>
      </span>
    </span>
  );
};

interface ToggleIconProps {
  checked: boolean;
  loading?: boolean;
  hasInfoTooltip: boolean;
}

const ToggleIcon = ({ checked, loading, hasInfoTooltip }: ToggleIconProps) => (
  <div className={cn('toggle-switch-icon', { 'has-info-tooltip': hasInfoTooltip })}>
    {hasInfoTooltip && <div className='info-tooltip-spacer' />}
    {loading ? (
      <Spinner size={14} color='currentColor' />
    ) : (
      <div className={cn('toggle-wrapper', checked ? 'on' : 'off')}>
        <div className='toggle-ball' />
      </div>
    )}
  </div>
);
